// Shared "calming ambient" web-audio hook used by the maximalist round-5
// birthday variants. Each variant passes a small spec and the hook builds
// a soft pad of detuned voices with a slow LFO swell. Master gain is
// intentionally tiny — these loops sit *under* the page, never dominate.

import { useEffect, useRef, useState } from "react";

export interface AmbientSpec {
  /** Triad / chord frequencies in Hz. 3–4 voices works well. */
  freqs: number[];
  /** Per-voice waveform. Default sine. */
  type?: OscillatorType;
  /** Master volume 0–1. Keep tiny (0.02–0.06). Default 0.04. */
  master?: number;
  /** Base LFO speed for the breathing swell. Default 0.12 Hz. */
  lfo?: number;
}

export function useAmbient(on: boolean, spec: AmbientSpec) {
  const ctxRef = useRef<AudioContext | null>(null);
  const oscsRef = useRef<OscillatorNode[]>([]);
  const masterRef = useRef<GainNode | null>(null);

  useEffect(() => {
    if (!on) {
      oscsRef.current.forEach((o) => { try { o.stop(); o.disconnect(); } catch { /* noop */ } });
      oscsRef.current = [];
      if (masterRef.current) {
        try { masterRef.current.disconnect(); } catch { /* noop */ }
        masterRef.current = null;
      }
      return;
    }
    if (typeof window === "undefined") return;
    type AudioCtxCtor = typeof AudioContext;
    const Ctor: AudioCtxCtor | undefined =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: AudioCtxCtor }).webkitAudioContext;
    if (!Ctor) return;
    if (!ctxRef.current) {
      try { ctxRef.current = new Ctor(); } catch { return; }
    }
    const c = ctxRef.current;
    if (!c) return;
    void c.resume();
    const master = c.createGain();
    master.gain.value = spec.master ?? 0.04;
    master.connect(c.destination);
    masterRef.current = master;
    const lfoBase = spec.lfo ?? 0.12;
    const oscs = spec.freqs.map((f, i) => {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = spec.type ?? "sine";
      o.frequency.value = f;
      o.detune.value = (i - (spec.freqs.length - 1) / 2) * 6;
      g.gain.value = 0.33;
      const lfo = c.createOscillator();
      const lfoGain = c.createGain();
      lfo.frequency.value = lfoBase + i * 0.03;
      lfoGain.gain.value = 0.18;
      lfo.connect(lfoGain);
      lfoGain.connect(g.gain);
      lfo.start();
      o.connect(g);
      g.connect(master);
      o.start();
      return o;
    });
    oscsRef.current = oscs;
    return () => {
      oscs.forEach((o) => { try { o.stop(); o.disconnect(); } catch { /* noop */ } });
      try { master.disconnect(); } catch { /* noop */ }
    };
  }, [on, spec.freqs, spec.type, spec.master, spec.lfo]);
}

export function useReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduce(m.matches);
    update();
    m.addEventListener?.("change", update);
    return () => m.removeEventListener?.("change", update);
  }, []);
  return reduce;
}
